/** @author PVCOMBANK */
import * as React from "react";
import { Paragraph, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { InputText, InputAutoComplete, InputGridListView, InputTextCurrency, TextInputMultiline } from "app/shared/components/form/input";
import { StyleSheet } from "react-native";
import { ACCOUNT } from "app/shared/services/services-model.type";
import { DataAutoTopUpCreate } from "../../reducer/types";
import { useSelector } from "react-redux";
import { getAutoTopUpStoreSelector } from "../../reducer/selectors";
import { ValidatePhoneField } from "app/shared/util/validate";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import { handlerAutoTopUpPrinces, handlerAutoTopUpServices, loadAutoTopUpServiceJson } from "../../auto-top-up-utils/auto-top-up.utils";
import { ServiceCode } from "app/shared/services/api-path";

export interface FormAutoTopUpProps {
  onSubmit: Function;
  accountDebit: ACCOUNT;
  onCancel: Function;
  requestCreate: Function;
  showModal: Function;
  closeModal: Function;
}

enum FormEnum {
  serviceType,
  serviceName,
  providerID,
  providerName,
  customerCode,
  paymentType,
  paymentDate,
  amount,
  note,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export type FormAutoTopUpData = {
  serviceType: string;
  serviceName: string;
  providerID: string;
  providerName: string;
  customerCode: string;
  paymentType: string;
  paymentDate: string;
  amount: string;
  note: string;
};

const Type = {
  PHONE: "100001",
  OTHER: "OTHER",
};

const FormAutoTopUp = (props: FormAutoTopUpProps) => {
  const { onSubmit, requestCreate, onCancel, accountDebit } = props;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();

  // Instance Form Method
  const { control, handleSubmit, getValues, setValue, trigger, clearErrors, watch } = useForm<FormAutoTopUpData>({
    defaultValues: {
      serviceType: "",
      serviceName: "",
      providerID: "",
      providerName: "",
      customerCode: "",
      note: "",
      paymentType: "Y",
      paymentDate: t("AUTO_TOPUP_LAN_KHUYEN_MAI_DAU_TIEN"),
      amount: "",
    },
    mode: "onChange",
  });

  const autoTopUpStore = useSelector(getAutoTopUpStoreSelector);

  const [serviceArray, setServiceArray] = React.useState<ItemCompleteProps[]>([]);
  const [providerArray, setProviderArray] = React.useState<ItemCompleteProps[]>([]);
  const [priceArray, setPriceArray] = React.useState<ItemCompleteProps[]>([]);
  const [topUpType, setTopUpType] = React.useState<string>("");

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
      }),
    [colors.text, colors.notification],
  );

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    serviceType: {
      required: {
        value: true,
        message: t("TOPUP_VUI_LONG_CHON_DICH_VU"),
      },
    },
    serviceName: {
      required: {
        value: false,
        message: "",
      },
    },
    providerID: {
      required: {
        value: true,
        message: t("AUTO_TOPUP_VUI_LONG_CHON_NHA_MANG"),
      },
    },
    providerName: {
      required: {
        value: false,
        message: "",
      },
    },
    customerCode: {
      required: {
        value: true,
        message: t("AUTO_TOPUP_VUI_LONG_NHAP_SO_DIEN_THOAI"),
      },
      validate: {
        phone: (value) => {
          if (topUpType === Type.PHONE && !ValidatePhoneField(value)) {
            return t("AUTO_TOPUP_SO_DIEN_THOAI_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
    amount: {
      required: {
        value: true,
        message: t("TOPUP_VUI_LONG_CHON_MENH_GIA"),
      },
      validate: {
        amountMin: (value) => {
          if (Number(value) < 10000) {
            return t("COMMON_SO_TIEN_KHONG_DUOC_DUOI_10000D").toString();
          }
          return true;
        },
      },
    },
    note: {
      required: {
        value: true,
        message: t("AUTO_TOPUP_VUI_LONG_NHAP_HO_VA_TEN"),
      },
    },
    paymentType: {
      required: {
        value: false,
        message: "",
      },
    },
    paymentDate: {
      required: {
        value: false,
        message: "",
      },
    },
  };

  const handleRequestCreate = (value: FormAutoTopUpData): void => {
    const dataUpdate: DataAutoTopUpCreate = {
      ACCOUNT_DEBIT: accountDebit,
      AMOUNT: value.amount,
      NOTE: value.note,
      SERVICE: { value: value.serviceType, label: value.serviceName },
      PROVIDER: { value: value.providerID, label: value.providerName },
      INQUIRY_CODE: value.customerCode,
      PAYMENT_DATE: { value: value.paymentType, label: value.paymentDate },
      SERVICE_CODE: ServiceCode.AUTO_TOP_UP,
    };
    requestCreate(dataUpdate);
  };

  /// MARK: - Action Handler
  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onClickProvider = () => {
    trigger("serviceType");
  };

  const checkSelectProvider = () => {
    if (getValues("serviceType").length) return false;
    return true;
  };

  const onSelectedProvider = (provider?: ItemCompleteProps) => {
    setValue("providerName", provider?.label || "");
    if (getValues("customerCode").length) {
      setValue("customerCode", "");
      clearErrors("customerCode");
    }
  };

  const onSelectedPrice = (price?: ItemCompleteProps) => {
    setValue("amount", price?.value || "");
    trigger("amount");
  };

  // set thông tin auto topUp
  const onBlurCustomer = () => {
    trigger("customerCode");
  };

  /// MARK: - Child Component
  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <InputText
        name="serviceName"
        control={control}
        rules={validateSchemeForm.serviceName}
        inputProps={{ editable: false }}
        label={t("TOPUP_DICH_VU")}
      />
      <Block row={isDesktop ? true : false} flex={1}>
        <Block flex={isDesktop ? 1 : undefined}>
          <InputAutoComplete
            rules={validateSchemeForm.providerID}
            name="providerID"
            options={providerArray}
            control={control}
            title={t("AUTO_TOPUP_NHA_MANG")}
            label={t("AUTO_TOPUP_CHON_NHA_MANG")}
            onClick={onClickProvider}
            checkSelect={checkSelectProvider()}
            onDismiss={onSelectedProvider}
          />
        </Block>
        <QueueSpacing size="huge" />
        <Block flex={isDesktop ? 1 : undefined}>
          <InputText
            name="customerCode"
            control={control}
            rules={validateSchemeForm.customerCode}
            label={t("AUTO_TOPUP_SO_DIEN_THOAI")}
            onBlur={onBlurCustomer}
          />
        </Block>
      </Block>

      <InputText name="note" control={control} rules={validateSchemeForm.note} label={t("TOPUP_HO_TEN")} />
      <TextInputMultiline
        name="paymentDate"
        inputProps={{ editable: false }}
        label={t("AUTO_TOPUP_NGAY_NAP")}
        value={getValues("paymentDate")}
      />
      <InputTextCurrency
        name="amount"
        keyboardType={"numeric"}
        control={control}
        rules={validateSchemeForm.amount}
        inputProps={{ editable: false }}
        label={t("AUTO_TOPUP_MENH_GIA")}
        rightCurrency={t("COMMON_VND")}
      />
      <StackSpacing size="medium" />
      <InputGridListView onSelected={onSelectedPrice} values={priceArray} />
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={() => onCancel()} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    loadAutoTopUpServiceJson().then(() => {
      const listService = handlerAutoTopUpServices();
      const listPrice = handlerAutoTopUpPrinces();
      setServiceArray(listService);
      setPriceArray(listPrice);
    });
  }, []);

  React.useEffect(() => {
    const _listProvider: ItemCompleteProps[] = [];
    if (autoTopUpStore?.resListProvider?.length) {
      autoTopUpStore.resListProvider.forEach((_item) => {
        _listProvider.push({ value: _item, label: _item });
      });
    }
    setProviderArray(_listProvider);
  }, [autoTopUpStore.resListProvider]);

  React.useEffect(() => {
    if (serviceArray.length) {
      const _services = serviceArray.filter((_item) => {
        return _item.value === Type.PHONE;
      });
      if (_services.length) {
        setValue("serviceType", _services[0].value);
        setValue("serviceName", _services[0].label);
      }
    }
  }, [serviceArray, setValue]);

  React.useEffect(() => {
    const subscription = watch(({ serviceType }) => {
      if (serviceType === Type.PHONE) {
        setTimeout(() => {
          setTopUpType(Type.PHONE);
        }, 0);
      } else if (serviceType !== Type.PHONE && serviceType !== "") {
        setTimeout(() => {
          setTopUpType(Type.OTHER);
        }, 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormAutoTopUp;
